import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  TrendingDown, 
  IndianRupee, 
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Download,
  PieChart,
  BarChart3,
  Wallet,
  Receipt,
  Target
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinancialsPage = () => {
  const [yearlyData, setYearlyData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [periodFilter, setPeriodFilter] = useState('year');
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const currentMonthNum = new Date().getMonth(); // 0-indexed
  const currentQuarterNum = Math.floor(currentMonthNum / 3) + 1; // 1-4
  const [selectedMonth, setSelectedMonth] = useState(currentMonthNum + 1); // 1-12
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarterNum); // 1-4
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const quarterMonths = {
    1: 'Jan-Mar',
    2: 'Apr-Jun',
    3: 'Jul-Sep',
    4: 'Oct-Dec'
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [yearlyRes, currentRes] = await Promise.all([
        axios.get(`${API}/financial-summary?year=${selectedYear}`),
        axios.get(`${API}/financial-summary/current-month`)
      ]);
      setYearlyData(yearlyRes.data);
      setCurrentMonth(currentRes.data);
    } catch (error) {
      toast.error('Error fetching financial data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on period
  const getFilteredData = () => {
    if (!yearlyData) return { monthlyData: [], summary: { total_income: 0, total_expenses: 0, net_profit_loss: 0 } };
    
    let filteredMonths = yearlyData.monthly_data;
    
    if (periodFilter === 'month') {
      // Selected month (1-12 → array index 0-11)
      filteredMonths = yearlyData.monthly_data.filter((_, idx) => idx === (selectedMonth - 1));
    } else if (periodFilter === 'quarter') {
      // Selected quarter (1-4)
      const quarterStart = (selectedQuarter - 1) * 3;
      filteredMonths = yearlyData.monthly_data.slice(quarterStart, quarterStart + 3);
    }
    // 'year' and 'all' show all months
    
    // Calculate summary for filtered data
    const summary = {
      total_income: filteredMonths.reduce((sum, m) => sum + (m.income || 0), 0),
      total_expenses: filteredMonths.reduce((sum, m) => sum + (m.expenses || 0), 0),
      net_profit_loss: filteredMonths.reduce((sum, m) => sum + (m.profit_loss || 0), 0)
    };
    
    return { monthlyData: filteredMonths, summary };
  };

  // Helper: human-readable period label
  const getPeriodLabel = () => {
    if (periodFilter === 'month') return `${monthNames[selectedMonth - 1]} ${selectedYear}`;
    if (periodFilter === 'quarter') return `Q${selectedQuarter} ${selectedYear} (${quarterMonths[selectedQuarter]})`;
    if (periodFilter === 'year') return `Year ${selectedYear}`;
    return 'All Time';
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  const exportToExcel = () => {
    if (!yearlyData) return;

    const exportData = yearlyData.monthly_data.map(m => ({
      'Month': m.month_name,
      'Income (₹)': m.income,
      'Expenses (₹)': m.expenses,
      'Profit/Loss (₹)': m.profit_loss
    }));

    // Add yearly summary
    exportData.push({});
    exportData.push({
      'Month': 'YEARLY TOTAL',
      'Income (₹)': yearlyData.yearly_summary.total_income,
      'Expenses (₹)': yearlyData.yearly_summary.total_expenses,
      'Profit/Loss (₹)': yearlyData.yearly_summary.net_profit_loss
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financial Summary');
    
    XLSX.writeFile(wb, `Financial_Summary_${selectedYear}.xlsx`);
    toast.success('Report exported successfully');
  };

  const getBarWidth = (value, max) => {
    if (max === 0) return 0;
    return Math.min((value / max) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const maxValue = yearlyData ? Math.max(
    ...yearlyData.monthly_data.map(m => Math.max(m.income, m.expenses))
  ) : 0;

  return (
    <div data-testid="financials-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Financial Summary</h1>
          <p className="text-slate-500 mt-1">Monthly tally - Income vs Expenses</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="select-year">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={exportToExcel}
            data-testid="export-financial-btn"
            className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'all', label: 'All Time' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`period-tab-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}

        {/* Month picker (visible when This Month selected) */}
        {periodFilter === 'month' && (
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[150px] h-9" data-testid="select-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, idx) => (
                <SelectItem key={idx + 1} value={(idx + 1).toString()}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Quarter picker (visible when This Quarter selected) */}
        {periodFilter === 'quarter' && (
          <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
            <SelectTrigger className="w-[170px] h-9" data-testid="select-quarter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
              <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
              <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
              <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Period-Based Summary Cards */}
      {yearlyData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-600 to-green-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(getFilteredData().summary.total_income)}</p>
                  <p className="text-xs text-white/70">
                    Income ({getPeriodLabel()})
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm bg-gradient-to-br from-red-500 to-red-600">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(getFilteredData().summary.total_expenses)}</p>
                  <p className="text-xs text-white/70">
                    Expenses ({getPeriodLabel()})
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={`border-0 shadow-sm ${getFilteredData().summary.net_profit_loss >= 0 ? 'bg-gradient-to-br from-brand-navy to-brand-navy/90' : 'bg-gradient-to-br from-amber-500 to-amber-600'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  {getFilteredData().summary.net_profit_loss >= 0 ? 
                    <ArrowUpRight className="h-6 w-6 text-white" /> : 
                    <ArrowDownRight className="h-6 w-6 text-white" />
                  }
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(Math.abs(getFilteredData().summary.net_profit_loss))}</p>
                  <p className="text-xs text-white/70">
                    {getFilteredData().summary.net_profit_loss >= 0 ? 'Profit' : 'Loss'} ({getPeriodLabel()})
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-500 to-purple-600">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{currentMonth?.pending_payments || 0}</p>
                  <p className="text-xs text-white/70">Pending Payments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Period Summary */}
      {yearlyData && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {getPeriodLabel()} Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Total Income</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(getFilteredData().summary.total_income)}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600 font-medium">Total Expenses</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(getFilteredData().summary.total_expenses)}</p>
              </div>
              <div className={`text-center p-4 rounded-lg ${getFilteredData().summary.net_profit_loss >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
                <p className={`text-sm font-medium ${getFilteredData().summary.net_profit_loss >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                  Net {getFilteredData().summary.net_profit_loss >= 0 ? 'Profit' : 'Loss'}
                </p>
                <p className={`text-2xl font-bold ${getFilteredData().summary.net_profit_loss >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                  {formatCurrency(Math.abs(getFilteredData().summary.net_profit_loss))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Chart */}
      {yearlyData && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Monthly Breakdown ({getPeriodLabel()})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {getFilteredData().monthlyData.map((month) => (
                <div key={month.month} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-600 w-24">{month.month_name.substring(0, 3)}</span>
                    <div className="flex-1 mx-4">
                      {/* Income Bar */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-green-600 w-16">Income</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{ width: `${getBarWidth(month.income, maxValue)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-green-700 w-24 text-right">{formatCurrency(month.income)}</span>
                      </div>
                      {/* Expense Bar */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 w-16">Expense</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full transition-all duration-500"
                            style={{ width: `${getBarWidth(month.expenses, maxValue)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-red-700 w-24 text-right">{formatCurrency(month.expenses)}</span>
                      </div>
                    </div>
                    <Badge 
                      className={`w-24 justify-center ${month.profit_loss >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} border-0`}
                    >
                      {month.profit_loss >= 0 ? '+' : ''}{formatCurrency(month.profit_loss)}
                    </Badge>
                  </div>
                </div>
              ))}
              {getFilteredData().monthlyData.length === 0 && (
                <p className="text-center text-slate-500 py-4">No data for selected period</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Table */}
      {yearlyData && (
        <Card className="border-0 shadow-sm" data-testid="monthly-details-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-brand-navy">
              Monthly Details ({getPeriodLabel()})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Month</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Income</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Expenses</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Profit/Loss</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredData().monthlyData.map((month) => (
                  <TableRow key={month.month} className="hover:bg-slate-50/50" data-testid={`monthly-row-${month.month}`}>
                    <TableCell className="font-medium">{month.month_name}</TableCell>
                    <TableCell className="text-right text-green-600 font-semibold">
                      {formatCurrency(month.income)}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-semibold">
                      {formatCurrency(month.expenses)}
                    </TableCell>
                    <TableCell className={`text-right font-bold ${month.profit_loss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {month.profit_loss >= 0 ? '+' : ''}{formatCurrency(month.profit_loss)}
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredData().monthlyData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-4">No data for selected period</TableCell>
                  </TableRow>
                )}
                {/* Total Row */}
                <TableRow className="bg-brand-navy/5 font-bold">
                  <TableCell className="text-brand-navy">
                    TOTAL ({getPeriodLabel()})
                  </TableCell>
                  <TableCell className="text-right text-green-700">
                    {formatCurrency(getFilteredData().summary.total_income)}
                  </TableCell>
                  <TableCell className="text-right text-red-700">
                    {formatCurrency(getFilteredData().summary.total_expenses)}
                  </TableCell>
                  <TableCell className={`text-right ${getFilteredData().summary.net_profit_loss >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {getFilteredData().summary.net_profit_loss >= 0 ? '+' : ''}
                    {formatCurrency(getFilteredData().summary.net_profit_loss)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Expense Category Breakdown for Current Month */}
      {currentMonth && Object.keys(currentMonth.expense_by_category).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Expense Categories ({currentMonth.month})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(currentMonth.expense_by_category)
                .sort(([,a], [,b]) => b - a)
                .map(([category, amount]) => (
                <div key={category} className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 truncate">{category}</p>
                  <p className="text-lg font-bold text-brand-navy">{formatCurrency(amount)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FinancialsPage;
